/**
 * Feedback Tabs Layout
 * Main container for tabbed feedback interface
 */

import { useState } from "react";
import { FeedbackBlock } from "@/types/interview-feedback.types";
import { DashboardSession } from "@/types/dashboard.types";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  LayoutDashboard,
  ChartBar,
  FileText,
  MessageCircle,
  Download,
  Share2,
  Loader2,
  Sparkles,
  RotateCcw,
} from "lucide-react";
import { FeedbackBlockRenderer } from "./FeedbackBlockRenderer";
import { AverageSummaryBlock } from "./AverageSummaryBlock";
import { processScore } from "@/utils/scoreUtils";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { generateQuizThunk, resetQuizState } from "@/store/slices/quizSlice";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";

interface FeedbackTabsLayoutProps {
  blocks: FeedbackBlock[];
  onShare?: () => void;
  onDownloadPDF?: () => void;
  meetingId?: string;
  session?: DashboardSession | null;
}

export const FeedbackTabsLayout = ({
  blocks,
  onShare,
  onDownloadPDF,
  meetingId,
  session,
}: FeedbackTabsLayoutProps) => {
  const [activeTab, setActiveTab] = useState("overview");
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { toast } = useToast();
  const { status, isPolling, loading } = useAppSelector((state) => state.quiz);
  const [generatingQuizId, setGeneratingQuizId] = useState<string | null>(null);

  const handleCreateQuiz = async () => {
    if (!meetingId) return;

    // If quiz is already completed, just navigate to it
    if (session?.quiz_status === "completed") {
      dispatch(resetQuizState(meetingId));
      navigate(`/meeting/${meetingId}/quiz/generated`);
      return;
    }

    // Otherwise, generate a new quiz
    if (session?.quiz_status === "not_started" || session?.quiz_status === "error" || !session?.quiz_status) {
      setGeneratingQuizId(meetingId);
      const resultAction = await dispatch(generateQuizThunk(meetingId));
      if (generateQuizThunk.rejected.match(resultAction)) {
        setGeneratingQuizId(null);
        toast({
          title: "Error",
          description: "Failed to start quiz generation.",
          variant: "destructive",
        });
      }
    }
  };

  // Group blocks by type
  const gaugeBlocks = blocks.filter((b) => b.type === "gauge");
  const scoreGridBlocks = blocks.filter((b) => b.type === "score_grid");
  const textBlocks = blocks.filter((b) => b.type === "text_block");
  const transcriptBlocks = blocks.filter((b) => b.type === "transcript");

  // Calculate average scores for Overview tab (group by title and average all blocks with same title)
  const averagedScoreGridBlocks = scoreGridBlocks.reduce((acc, block) => {
    const existingIndex = acc.findIndex((b) => b.title === block.title);

    if (existingIndex === -1) {
      // First block with this title - add it to accumulator
      acc.push({
        ...block,
        items: block.items.map((item) => ({
          ...item,
          score: item.score,
          count: 1, // Track how many blocks we've averaged
        })),
      });
    } else {
      // Block with this title already exists - add scores to average
      const existing = acc[existingIndex];
      existing.items = existing.items.map((existingItem, idx) => {
        const newItem = block.items[idx];
        return {
          ...existingItem,
          score: existingItem.score + (newItem?.score || 0),
          count: (existingItem as any).count + 1,
        };
      });
    }

    return acc;
  }, [] as any[]);

  // Calculate final averages
  const uniqueScoreGridBlocks = averagedScoreGridBlocks.map((block) => ({
    ...block,
    items: block.items.map((item: any) => ({
      ...item,
      score: item.score / item.count, // Calculate average
      // Remove count property
      count: undefined,
    })),
  }));

  // Calculate total number of individual metrics (items) from unique score_grid blocks only
  const totalMetricsCount = uniqueScoreGridBlocks.reduce((sum, block) => {
    return sum + (block.items?.length || 0);
  }, 0);

  // Get overall score for overview
  const overallScore = gaugeBlocks[0];
  const overallScoreData = overallScore
    ? processScore(overallScore.value, overallScore.maxValue)
    : null;

  return (
    <Tabs
      value={activeTab}
      onValueChange={setActiveTab}
      className="w-full space-y-6"
    >
      {/* Sticky Tab Navigation */}
      <div className="sticky top-16 z-10 bg-background/95 backdrop-blur-md border-b border-border/40 pb-4">
        <TabsList className="w-full justify-start h-auto p-2 bg-muted/50 gap-2 overflow-x-auto scrollbar-hide min-w-0">
          <TabsTrigger
            value="overview"
            className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
          >
            <LayoutDashboard className="w-4 h-4" />
            <span>Overview</span>
          </TabsTrigger>

          <TabsTrigger
            value="scores"
            className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
          >
            <ChartBar className="w-4 h-4" />
            <span>Detailed Scores</span>
            <Badge variant="secondary" className="ml-1">
              {totalMetricsCount}
            </Badge>
          </TabsTrigger>

          <TabsTrigger
            value="analysis"
            className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
          >
            <FileText className="w-4 h-4" />
            <span>Analysis</span>
            <Badge variant="secondary" className="ml-1">
              {textBlocks.length}
            </Badge>
          </TabsTrigger>

          <TabsTrigger
            value="transcript"
            className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
          >
            <MessageCircle className="w-4 h-4" />
            <span>Transcript</span>
            <Badge variant="secondary" className="ml-1">
              {transcriptBlocks.length}
            </Badge>
          </TabsTrigger>
        </TabsList>
      </div>

      {/* Overview Tab */}
      <TabsContent value="overview" className="space-y-6">
        {/* Header with Score and Actions */}
        <Card className="p-6 border-border/50 backdrop-blur-sm bg-card/80">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            {/* Overall Score Display */}
            {overallScoreData && (
              <div className="flex items-center gap-6">
                {/* Circular Gauge Mini */}
                <div className="relative w-32 h-32">
                  <svg
                    className="w-full h-full transform -rotate-90"
                    viewBox="0 0 120 120"
                  >
                    <circle
                      cx="60"
                      cy="60"
                      r="50"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="10"
                      className="text-muted/20"
                    />
                    <circle
                      cx="60"
                      cy="60"
                      r="50"
                      fill="none"
                      stroke={overallScoreData.color}
                      strokeWidth="10"
                      strokeLinecap="round"
                      strokeDasharray={`${
                        (overallScoreData.percentage / 100) * 314
                      } 314`}
                      className="transition-all duration-1000"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <div
                      className="text-3xl font-bold"
                      style={{ color: overallScoreData.color }}
                    >
                      {overallScoreData.value.toFixed(1)}
                    </div>
                    <div className="text-xs text-muted-foreground">/ 10</div>
                  </div>
                </div>

                {/* Score Info */}
                <div className="space-y-2">
                  <h3 className="text-2xl font-bold text-foreground">
                    Overall Performance
                  </h3>
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: overallScoreData.color }}
                    />
                    <span className="text-lg font-medium">
                      {overallScoreData.label}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              {/* <Button
                variant="outline"
                size="default"
                onClick={onShare}
                className="gap-2"
              >
                <Share2 className="w-4 h-4" />
                Share
              </Button>*/}
              <Button
                variant="default"
                size="default"
                onClick={onDownloadPDF}
                className="gap-2"
              >
                <Download className="w-4 h-4" />
                Download PDF
              </Button>
              
              {/* Quiz Button */}
              {meetingId && transcriptBlocks.length > 0 && (
                <Button
                  variant={session?.quiz_status === "completed" ? "outline" : "default"}
                  size="default"
                  onClick={handleCreateQuiz}
                  disabled={
                    (isPolling && generatingQuizId === meetingId) ||
                    (loading && generatingQuizId === meetingId) ||
                    session?.quiz_status === "processing"
                  }
                  className={
                    session?.quiz_status === "completed"
                      ? "gap-2 border-primary/20 hover:bg-primary/5 text-primary hover:text-primary"
                      : "gap-2 bg-gradient-to-r from-primary to-accent hover:opacity-90 transition-opacity"
                  }
                >
                  {(isPolling && generatingQuizId === meetingId) ||
                  (loading && generatingQuizId === meetingId) ||
                  session?.quiz_status === "processing" ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Generating...
                    </>
                  ) : session?.quiz_status === "completed" ? (
                    <>
                      <RotateCcw className="w-4 h-4" />
                      Retake Quiz
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      Start Quiz
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </Card>

        {/* Key Scores Preview - Average Summary */}
        {uniqueScoreGridBlocks.length > 0 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground">
              Key Metrics
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {uniqueScoreGridBlocks.map((block, index) => (
                <Card
                  key={`overview-${block.type}-${block.position}-${index}`}
                  className="p-4 border-border/50 backdrop-blur-sm bg-card/80"
                >
                  <AverageSummaryBlock block={block} />
                </Card>
              ))}
            </div>
            <Button
              variant="ghost"
              onClick={() => setActiveTab("scores")}
              className="w-full"
            >
              View full scores ({totalMetricsCount} metrics) →
            </Button>
          </div>
        )}

        {/* Top Insights Preview */}
        {textBlocks.length > 0 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground">
              Key Insights
            </h3>
            {textBlocks.slice(0, 2).map((block, index) => (
              <Card
                key={`${block.type}-${block.position}-${index}`}
                className="p-4 border-border/50 backdrop-blur-sm bg-card/80"
              >
                <FeedbackBlockRenderer block={block} />
              </Card>
            ))}
            {textBlocks.length > 2 && (
              <Button
                variant="ghost"
                onClick={() => setActiveTab("analysis")}
                className="w-full"
              >
                View full analysis ({textBlocks.length} sections) →
              </Button>
            )}
          </div>
        )}
      </TabsContent>

      {/* Detailed Scores Tab */}
      <TabsContent value="scores" className="space-y-6">
        {uniqueScoreGridBlocks.length === 0 ? (
          <Card className="p-8 text-center">
            <p className="text-muted-foreground">
              No detailed scores available.
            </p>
          </Card>
        ) : (
          uniqueScoreGridBlocks.map((block, index) => (
            <Card
              key={`scores-${block.type}-${block.position}-${index}`}
              className="p-6 border-border/50 backdrop-blur-sm bg-card/80"
            >
              <FeedbackBlockRenderer block={block} />
            </Card>
          ))
        )}
      </TabsContent>

      {/* Analysis Tab */}
      <TabsContent value="analysis" className="space-y-6">
        {textBlocks.length === 0 ? (
          <Card className="p-8 text-center">
            <p className="text-muted-foreground">No analysis available.</p>
          </Card>
        ) : (
          textBlocks.map((block, index) => (
            <Card
              key={`${block.type}-${block.position}-${index}`}
              className="p-6 border-border/50 backdrop-blur-sm bg-card/80"
            >
              <FeedbackBlockRenderer block={block} />
            </Card>
          ))
        )}
      </TabsContent>

      {/* Transcript Tab */}
      <TabsContent value="transcript" className="space-y-6">
        {transcriptBlocks.length === 0 ? (
          <Card className="p-8 text-center">
            <p className="text-muted-foreground">No transcript available.</p>
          </Card>
        ) : (
          transcriptBlocks.map((block, index) => (
            <Card
              key={`${block.type}-${block.position}-${index}`}
              className="p-6 border-border/50 backdrop-blur-sm bg-card/80"
            >
              <FeedbackBlockRenderer block={block} />
            </Card>
          ))
        )}
      </TabsContent>
    </Tabs>
  );
};
